import { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar, Plus, Clock, User, Edit, Trash2, CheckCircle2, XCircle, Eye, EyeOff } from 'lucide-react';
import AppointmentFormDialog from '@/components/AppointmentFormDialog';
import { toast } from 'sonner';

const Appointments = () => {
  const [appointments, setAppointments] = useState([]);
  const [patients, setPatients] = useState({});
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [showCanceled, setShowCanceled] = useState(false); // Hide canceled by default

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [appointmentsRes, patientsRes] = await Promise.all([
        axios.get(`${API}/appointments`),
        axios.get(`${API}/patients`)
      ]);
      
      setAppointments(appointmentsRes.data);
      
      // Create patients lookup
      const patientsMap = {};
      patientsRes.data.forEach(p => {
        patientsMap[p.id] = p;
      });
      setPatients(patientsMap);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAppointmentSaved = () => {
    fetchData();
    setShowDialog(false);
    setEditingAppointment(null);
  };

  const handleEdit = (appointment) => {
    setEditingAppointment(appointment);
    setShowDialog(true);
  };

  const handleDelete = async (appointmentId) => {
    if (!window.confirm('⚠️ SUPPRIMER DÉFINITIVEMENT ce rendez-vous ?\n\nCette action est irréversible. Le rendez-vous sera complètement supprimé de la base de données.')) {
      return;
    }

    try {
      console.log('Deleting appointment:', appointmentId);
      await axios.delete(`${API}/appointments/${appointmentId}`);
      console.log('Delete successful, refreshing data...');
      toast.success('✅ Rendez-vous supprimé - actualisation...');
      
      // Force complete page refresh to bypass any React state issues
      setTimeout(() => {
        window.location.href = window.location.pathname;
      }, 500);
    } catch (error) {
      console.error('Error deleting appointment:', error);
      toast.error('❌ Erreur lors de la suppression du rendez-vous');
    }
  };

  const handleStatusChange = async (appointmentId, newStatus) => {
    const statusMessages = {
      termine: '✅ Rendez-vous marqué comme terminé',
      annule: '⚠️ Rendez-vous annulé (reste dans le système)',
      en_cours: '🔵 Rendez-vous en cours',
      prevu: '📅 Rendez-vous planifié'
    };

    try {
      await axios.patch(`${API}/appointments/${appointmentId}/status?statut=${newStatus}`);
      toast.success(statusMessages[newStatus] || 'Statut mis à jour');
      fetchData();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('❌ Erreur lors de la mise à jour du statut');
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', {
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const getTypeLabel = (type) => {
    const types = {
      consultation: 'Consultation',
      perfusion: 'Perfusion IV',
      suivi: 'Suivi',
      controle: 'Contrôle'
    };
    return types[type] || type;
  };

  const getStatusColor = (statut) => {
    const colors = {
      prevu: 'bg-blue-100 text-blue-700',
      en_cours: 'bg-yellow-100 text-yellow-700',
      termine: 'bg-green-100 text-green-700',
      annule: 'bg-red-100 text-red-700'
    };
    return colors[statut] || 'bg-gray-100 text-gray-700';
  };

  const getStatusLabel = (statut) => {
    const labels = {
      prevu: 'Prévu',
      en_cours: 'En cours',
      termine: 'Terminé',
      annule: 'Annulé'
    };
    return labels[statut] || statut;
  };

  // Filter appointments based on showCanceled toggle
  const filteredAppointments = showCanceled 
    ? appointments 
    : appointments.filter(apt => apt.statut !== 'annule');

  // Count canceled appointments
  const canceledCount = appointments.filter(apt => apt.statut === 'annule').length;

  // Group appointments by date
  const groupedAppointments = filteredAppointments.reduce((groups, appointment) => {
    const date = appointment.start.split('T')[0];
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(appointment);
    return groups;
  }, {});

  const sortedDates = Object.keys(groupedAppointments).sort().reverse();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="appointments-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Rendez-vous</h1>
          <p className="text-gray-600 mt-1">Gestion du calendrier et des rendez-vous</p>
        </div>
        <div className="flex items-center space-x-3">
          {canceledCount > 0 && (
            <Button
              variant="outline"
              onClick={() => setShowCanceled(!showCanceled)}
              className="flex items-center space-x-2"
            >
              {showCanceled ? (
                <>
                  <EyeOff className="w-4 h-4" />
                  <span>Masquer annulés ({canceledCount})</span>
                </>
              ) : (
                <>
                  <Eye className="w-4 h-4" />
                  <span>Afficher annulés ({canceledCount})</span>
                </>
              )}
            </Button>
          )}
          <Button
            onClick={() => {
              setEditingAppointment(null);
              setShowDialog(true);
            }}
            data-testid="add-appointment-button"
            style={{
              background: 'linear-gradient(135deg, #0066cc 0%, #4a90e2 100%)',
              color: 'white'
            }}
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouveau Rendez-vous
          </Button>
        </div>
      </div>

      {sortedDates.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-16 text-gray-500">
              <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium mb-2">Aucun rendez-vous planifié</p>
              <p className="text-sm">Commencez par créer un nouveau rendez-vous</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {sortedDates.map((date) => {
            const dateObj = new Date(date + 'T00:00:00');
            const formattedDate = new Intl.DateTimeFormat('fr-FR', {
              weekday: 'long',
              day: '2-digit',
              month: 'long',
              year: 'numeric'
            }).format(dateObj);

            return (
              <Card key={date} data-testid={`appointments-date-${date}`}>
                <CardHeader>
                  <CardTitle className="flex items-center text-lg capitalize">
                    <Calendar className="w-5 h-5 mr-2 text-blue-600" />
                    {formattedDate}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {groupedAppointments[date]
                      .sort((a, b) => new Date(a.start) - new Date(b.start))
                      .map((appointment) => {
                        const patient = patients[appointment.patient_id];
                        
                        return (
                          <div
                            key={appointment.id}
                            className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                            data-testid={`appointment-${appointment.id}`}
                          >
                            <div className="flex-1 space-y-2">
                              <div className="flex items-center space-x-4">
                                <div className="flex items-center space-x-2">
                                  <Clock className="w-4 h-4 text-gray-500" />
                                  <span className="font-medium text-gray-900">
                                    {formatTime(appointment.start)} - {formatTime(appointment.end)}
                                  </span>
                                </div>
                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(appointment.statut)}`}>
                                  {getStatusLabel(appointment.statut)}
                                </span>
                                <span className="px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                                  {getTypeLabel(appointment.type)}
                                </span>
                              </div>
                              
                              {patient && (
                                <div className="flex items-center space-x-2 text-sm text-gray-600">
                                  <User className="w-4 h-4" />
                                  <span>{patient.identite.prenom} {patient.identite.nom}</span>
                                </div>
                              )}
                              
                              {appointment.notes && (
                                <p className="text-sm text-gray-600">{appointment.notes}</p>
                              )}
                            </div>

                            <div className="flex items-center space-x-2 ml-4">
                              {appointment.statut === 'prevu' && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleStatusChange(appointment.id, 'termine')}
                                  className="hover:bg-green-50 hover:text-green-600 hover:border-green-200"
                                  data-testid={`complete-appointment-${appointment.id}`}
                                  title="✅ Marquer comme terminé"
                                >
                                  <CheckCircle2 className="w-4 h-4" />
                                </Button>
                              )}
                              {appointment.statut === 'prevu' && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleStatusChange(appointment.id, 'annule')}
                                  className="hover:bg-orange-50 hover:text-orange-600 hover:border-orange-200"
                                  data-testid={`cancel-appointment-${appointment.id}`}
                                  title="⚠️ Annuler (garde le rendez-vous dans le système)"
                                >
                                  <XCircle className="w-4 h-4" />
                                </Button>
                              )}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleEdit(appointment)}
                                data-testid={`edit-appointment-${appointment.id}`}
                                title="✏️ Modifier le rendez-vous"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDelete(appointment.id)}
                                className="hover:bg-red-50 hover:text-red-600 hover:border-red-200"
                                data-testid={`delete-appointment-${appointment.id}`}
                                title="🗑️ SUPPRIMER DÉFINITIVEMENT (irréversible)"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <AppointmentFormDialog
        open={showDialog}
        onClose={() => {
          setShowDialog(false);
          setEditingAppointment(null);
        }}
        onSuccess={handleAppointmentSaved}
        appointment={editingAppointment}
      />
    </div>
  );
};

export default Appointments;
